import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Minus, Plus } from 'lucide-react';
import { cn } from '../lib/utils';

// D5 — one reusable -/+ stepper for every increment/decrement number field.
// Both input methods always work: tap/hold the -/+ buttons OR type directly.
// UI/interaction only — never changes the costing engine, money math or data.
//
// Props:
//   value, onChange(number)         controlled value + change callback
//   step      (default 1)           amount per -/+ press
//   decimals  (0 | n | true=2)      decimal places allowed (0 = whole numbers)
//   min, max                        clamped range (+ stops at max, - at min)
//   suffix                          small unit label inside the control (e.g. "%")
//   size      ('sm' | 'md')         button/input sizing
//   className / inputClassName      style overrides (e.g. input width)
export const NumberStepper = ({
  value, onChange, step = 1, decimals = 0, min, max, suffix,
  disabled = false, size = 'sm', className, inputClassName, 'data-testid': testId,
}) => {
  const places = decimals === true ? 2 : (decimals || 0);
  const allowNeg = min == null || min < 0;

  const round = useCallback((n) => {
    const f = Math.pow(10, places);
    return Math.round((n + Number.EPSILON) * f) / f;
  }, [places]);
  const clamp = useCallback((n) => {
    if (min != null && n < min) return min;
    if (max != null && n > max) return max;
    return n;
  }, [min, max]);

  const numValue = Number(value);
  const safeValue = Number.isFinite(numValue) ? numValue : 0;
  const fmt = useCallback((n) => (places > 0 ? Number(n).toFixed(places) : String(Math.trunc(n))), [places]);

  const [text, setText] = useState(fmt(safeValue));
  const focused = useRef(false);
  const valueRef = useRef(safeValue);
  useEffect(() => { valueRef.current = safeValue; if (!focused.current) setText(fmt(safeValue)); }, [safeValue, fmt]);

  const sanitize = (raw) => {
    let s = String(raw);
    if (places > 0) {
      s = s.replace(/[^0-9.-]/g, '');
      const parts = s.split('.');
      if (parts.length > 2) s = `${parts[0]}.${parts.slice(1).join('')}`;
      const [int, dec] = s.split('.');
      if (dec != null) s = `${int}.${dec.slice(0, places)}`;
    } else {
      s = s.replace(/[^0-9-]/g, '');
    }
    s = allowNeg ? s.replace(/(?!^)-/g, '') : s.replace(/-/g, '');
    return s;
  };

  const handleInput = (e) => {
    const s = sanitize(e.target.value);
    setText(s);
    if (s === '' || s === '-' || s === '.') return;
    const n = parseFloat(s);
    if (Number.isFinite(n)) onChange(clamp(round(n))); // don't reformat mid-typing
  };
  const handleBlur = () => {
    focused.current = false;
    let n = parseFloat(text);
    if (!Number.isFinite(n)) n = min != null ? min : 0;
    const c = clamp(round(n));
    onChange(c);
    setText(fmt(c));
  };

  const timers = useRef({});
  const stepBy = (dir) => {
    const c = clamp(round(valueRef.current + dir * step));
    valueRef.current = c;
    onChange(c);
    setText(fmt(c));
  };
  const startHold = (dir) => {
    stepBy(dir);
    timers.current.to = setTimeout(() => { timers.current.iv = setInterval(() => stepBy(dir), 80); }, 320);
  };
  const endHold = () => { clearTimeout(timers.current.to); clearInterval(timers.current.iv); timers.current = {}; };
  useEffect(() => () => endHold(), []);

  const atMin = min != null && safeValue <= min;
  const atMax = max != null && safeValue >= max;
  const sz = size === 'md'
    ? { btn: 'h-9 w-9', input: 'h-9 text-sm', icon: 16 }
    : { btn: 'h-7 w-7', input: 'h-7 text-xs', icon: 13 };

  const btnCls = 'grid shrink-0 place-items-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40';

  return (
    <div data-testid={testId}
      className={cn('inline-flex items-center overflow-hidden rounded-lg border bg-card', disabled && 'opacity-50', className)}>
      <button type="button" tabIndex={-1} aria-label="Decrease" disabled={disabled || atMin}
        data-testid={testId ? `${testId}-dec` : undefined}
        onPointerDown={(e) => { e.preventDefault(); if (!disabled && !atMin) startHold(-1); }}
        onPointerUp={endHold} onPointerLeave={endHold} onPointerCancel={endHold}
        className={cn(btnCls, sz.btn)}>
        <Minus size={sz.icon} />
      </button>
      <input
        inputMode={places > 0 ? 'decimal' : 'numeric'} disabled={disabled} value={text}
        onFocus={() => { focused.current = true; }} onChange={handleInput} onBlur={handleBlur}
        onClick={(e) => e.stopPropagation()}
        data-testid={testId ? `${testId}-input` : undefined}
        className={cn('min-w-0 flex-1 border-x bg-transparent text-center font-num outline-none focus-visible:bg-muted/40', sz.input, inputClassName)} />
      {suffix && <span className="px-1 text-[10px] text-muted-foreground">{suffix}</span>}
      <button type="button" tabIndex={-1} aria-label="Increase" disabled={disabled || atMax}
        data-testid={testId ? `${testId}-inc` : undefined}
        onPointerDown={(e) => { e.preventDefault(); if (!disabled && !atMax) startHold(1); }}
        onPointerUp={endHold} onPointerLeave={endHold} onPointerCancel={endHold}
        className={cn(btnCls, sz.btn)}>
        <Plus size={sz.icon} />
      </button>
    </div>
  );
};
